import { Banner, LoadingSkeleton } from '@boltenergy-be/design-system'
import { convertAmountOfSolarPanelsToKva, convertKvaToInjection } from 'utils/conversion'
import {
  BASE_SIMULATION_PRICE_REQUEST,
  INJECTION_ROI_MULTIPLIER
} from 'features/injection-tariff/components/injection-simulation-form/InjectionSimulationForm.constants'
import { useSimulatePriceQuery } from 'store/api/bolt-api'
import { formatAmount } from 'utils/format'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { useEffect, useMemo, useState } from 'react'
import debounce from 'debounce'
import { InjectionRoiBannerProps } from './types'
import { useTranslation } from 'next-i18next/pages'

const InjectionRoiBanner = ({ solarKva, amountOfSolarPanels, injection }: InjectionRoiBannerProps) => {
  // i18n
  const { t } = useTranslation('simulation', { keyPrefix: 'steps.usage.roi' })
  const language = useGetLanguage()

  // Constants
  const solarKvaAsInjection = solarKva ? convertKvaToInjection(solarKva) : 0
  const solarPanelsAsKva = amountOfSolarPanels ? convertAmountOfSolarPanelsToKva(amountOfSolarPanels) : 0
  const solarPanelsAsInjection = solarPanelsAsKva ? convertKvaToInjection(solarPanelsAsKva) : 0
  const totalInjection = solarKvaAsInjection + solarPanelsAsInjection + (injection ?? 0)

  // Local state
  const [debouncedInjection, setDebouncedInjection] = useState<number>(totalInjection)

  // Redux queries
  const { data, isFetching } = useSimulatePriceQuery(
    {
      ...BASE_SIMULATION_PRICE_REQUEST,
      meterUsage: {
        electricity: {
          consumption: { single: 1 },
          injection: { single: debouncedInjection * INJECTION_ROI_MULTIPLIER }
        }
      }
    },
    // Clearing the number input yields NaN, which JSON.stringify sends as null & makes the price simulation API reject
    { skip: !Number.isFinite(debouncedInjection) || debouncedInjection <= 0 }
  )

  const debouncedSetInjection = useMemo(() => debounce(setDebouncedInjection, 500), [])

  useEffect(() => {
    debouncedSetInjection(totalInjection)
  }, [totalInjection])

  useEffect(() => {
    return () => debouncedSetInjection.clear()
  }, [])

  // Constants
  const returnOnInjection = data ? Math.abs(data.price.electricity.totalInjection * 12) : 0

  if (!returnOnInjection && !isFetching) {
    return null
  }

  return (
    <Banner
      type="positive"
      className="grid-col-full"
      icon="sun"
      title={!isFetching ? t('title', { roi: formatAmount(returnOnInjection, language) }) : undefined}
    >
      {isFetching ? (
        <LoadingSkeleton className="gap-100">
          <LoadingSkeleton.Rectangle width={300} height={24} className="mb-100" />
          <LoadingSkeleton.Paragraph lines={2} />
        </LoadingSkeleton>
      ) : (
        t('description')
      )}
    </Banner>
  )
}

export default InjectionRoiBanner
